import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Collapse
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
  Download
} from '@mui/icons-material';
import axios from 'axios';

const ImportCSVDialog = ({ open, onClose, onSuccess, apiEndpoint, title }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showErrors, setShowErrors] = useState(true);
  const [showSuccess, setShowSuccess] = useState(true);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResults(null);
    } else {
      alert('Veuillez sélectionner un fichier CSV');
    }
  };

  const handleImport = async () => {
    if (!file) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const csvData = e.target.result;
        
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            apiEndpoint,
            { csvData },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.data.success) {
            setResults(response.data.data);
            if (response.data.data.errors.length === 0) {
              // Succès total, on peut fermer après 3 secondes
              setTimeout(() => {
                onSuccess();
                handleClose();
              }, 3000);
            }
          }
        } catch (error) {
          console.error('Erreur import:', error);
          alert(error.response?.data?.message || 'Erreur lors de l\'import');
        } finally {
          setLoading(false);
        }
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Erreur lecture fichier:', error);
      alert('Erreur lors de la lecture du fichier');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onClose();
  };

  const downloadTemplate = () => {
    const template = `nom_medicament,quantite,seuil_alerte,numero_lot,date_expiration
Amoxicilline 500mg,100,20,LOT2024001,2025-12-31
Antiparasitaire Chien,50,10,LOT2024002,2026-06-30
Vitamine B12,200,30,LOT2024003,2025-09-15`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_import_stock.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <Button
            size="small"
            startIcon={<Download />}
            onClick={downloadTemplate}
          >
            Télécharger modèle
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Format CSV attendu :</strong>
          </Typography>
          <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
            nom_medicament,quantite,seuil_alerte,numero_lot,date_expiration
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • <strong>nom_medicament</strong> et <strong>quantite</strong> sont obligatoires<br/>
            • Les médicaments inexistants seront créés automatiquement<br/>
            • Les quantités seront ajoutées aux stocks existants
          </Typography>
        </Alert>

        {/* Upload */}
        {!results && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                size="large"
              >
                Sélectionner un fichier CSV
              </Button>
            </label>
            
            {file && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Fichier sélectionné : <strong>{file.name}</strong>
              </Typography>
            )}
          </Box>
        )}

        {/* Résultats */}
        {results && (
          <Box>
            {/* Résumé */}
            <Alert 
              severity={results.errors.length === 0 ? 'success' : 'warning'} 
              sx={{ mb: 2 }}
            >
              <Typography variant="body1">
                <strong>Import terminé :</strong> {results.success.length} succès, {results.errors.length} erreurs
              </Typography>
            </Alert>

            {/* Succès */}
            {results.success.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer', mb: 1 }}
                  onClick={() => setShowSuccess(!showSuccess)}
                >
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Succès ({results.success.length})
                  </Typography>
                  <IconButton size="small">
                    {showSuccess ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={showSuccess}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Médicament</TableCell>
                          <TableCell>Action</TableCell>
                          <TableCell align="right">Anc. Qté</TableCell>
                          <TableCell align="right">Ajouté</TableCell>
                          <TableCell align="right">Nouv. Qté</TableCell>
                          <TableCell align="right">Seuil</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.success.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.nom_medicament}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.action} 
                                size="small" 
                                color={item.action === 'Créé' ? 'success' : 'primary'}
                              />
                            </TableCell>
                            <TableCell align="right">{item.ancienne_quantite}</TableCell>
                            <TableCell align="right">
                              <strong style={{ color: 'green' }}>+{item.quantite_ajoutee}</strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>{item.nouvelle_quantite}</strong>
                            </TableCell>
                            <TableCell align="right">{item.seuil_alerte}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}

            {/* Erreurs */}
            {results.errors.length > 0 && (
              <Box>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer', mb: 1 }}
                  onClick={() => setShowErrors(!showErrors)}
                >
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Erreurs ({results.errors.length})
                  </Typography>
                  <IconButton size="small">
                    {showErrors ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={showErrors}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ligne</TableCell>
                          <TableCell>Médicament</TableCell>
                          <TableCell>Erreur</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.ligne}</TableCell>
                            <TableCell>{error.nom_medicament}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {error.erreur}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {results ? 'Fermer' : 'Annuler'}
        </Button>
        {!results && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!file || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {loading ? 'Import en cours...' : 'Importer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCSVDialog;
